import React, { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/Toast";
import * as XLSX from "xlsx";

const Students = () => {
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useAuth();

  // Form state — route_code for display, route_id resolved from bus code
  const [formData, setFormData] = useState({
    student_id: null,
    registration_number: "",
    full_name: "",
    grade: "",
    route_code: "", // for the route dropdown (unique)
    route_name: "", // for displaying route name in read-only mode
    route_id: "", // resolved from bus code selection
    stop_id: "",
    stop_name: "", // for displaying stop name in read-only mode
    vehicle_code: "",
    parent_id: "",
  });
  const [uniqueRoutes, setUniqueRoutes] = useState([]); // deduplicated by route_code
  const [busCodes, setBusCodes] = useState([]); // { route_id, vehicle_code }
  const [stops, setStops] = useState([]);
  const [parentName, setParentName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [secondaryPhone, setSecondaryPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const loadStudents = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const endpoint =
        user.role === "parent"
          ? `/parents/${user.user_id}/students`
          : "/students";
      const res = await apiFetch(endpoint);
      setStudents(res.data || []);
    } catch (error) {
      toast.error(error.message || "Failed to load students");
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFormDependencies = async () => {
    try {
      const rRes = await apiFetch("/routes/by-code");
      setUniqueRoutes(rRes.data || []);
    } catch (e) {}
  };

  useEffect(() => {
    loadStudents();
    if (
      user &&
      (user.role === "superadmin" ||
        user.role === "admin" ||
        user.role === "operator")
    )
      loadFormDependencies();
  }, [user]);

  // When route_code changes → fetch bus codes AND stops (stops are same across all bus codes for a route)
  useEffect(() => {
    if (!formData.route_code) {
      setBusCodes([]);
      setStops([]);
      return;
    }

    apiFetch(
      `/routes/bus-codes?route_code=${encodeURIComponent(formData.route_code)}`,
    )
      .then(async (res) => {
        const codes = res.data || [];
        setBusCodes(codes);
        // Fetch stops from the first matching route_id (stops are identical across all bus codes)
        if (codes.length > 0) {
          const routeRes = await apiFetch(`/routes/${codes[0].route_id}`);
          setStops(routeRes.data?.stops || []);
        } else {
          // No bus codes yet — fetch stops via the routes list
          const rRes = await apiFetch("/routes");
          const match = (rRes.data || []).find(
            (r) => r.route_code === formData.route_code,
          );
          if (match) {
            const routeRes = await apiFetch(`/routes/${match.route_id}`);
            setStops(routeRes.data?.stops || []);
          } else {
            setStops([]);
          }
        }
      })
      .catch(() => {
        setBusCodes([]);
        setStops([]);
      });
  }, [formData.route_code]);

  // When bus code changes → silently update route_id only, never touch stop_id
  useEffect(() => {
    if (!formData.vehicle_code || busCodes.length === 0) return;
    const entry = busCodes.find(
      (b) => b.vehicle_code === formData.vehicle_code,
    );
    if (entry && entry.route_id !== formData.route_id) {
      setFormData((prev) => ({ ...prev, route_id: entry.route_id }));
    }
  }, [formData.vehicle_code, busCodes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let extra = {};
    if (name === "route_code")
      extra = { route_id: "", stop_id: "", vehicle_code: "" };
    if (name === "vehicle_code") {
      // resolve route_id silently — do NOT clear stop_id
      const entry = busCodes.find((b) => b.vehicle_code === value);
      extra = { route_id: entry?.route_id || "" };
    }
    setFormData({ ...formData, [name]: value, ...extra });
  };

  const handleEditOpen = async (s) => {
    // Find the route_code for this student's route_id
    let routeCode = "";
    let routeName = "";
    let stopName = s.stop_name || s.stop?.stop_name || ""; // Use existing stop_name from student
    let stopsData = [];
    let busCodesData = [];
    let routesData = [];

    try {
      // Fetch all routes to populate uniqueRoutes and get route names
      const routesRes = await apiFetch("/routes/by-code");
      routesData = routesRes.data || [];
      setUniqueRoutes(routesData);

      // Find this student's route in the list
      const studentRoute = routesData.find((r) => r.route_id === s.route_id);
      if (studentRoute) {
        routeCode = studentRoute.route_code || "";
        routeName = studentRoute.route_name || "";
      } else {
        // Fallback: fetch the specific route if not in list
        const rRes = await apiFetch(`/routes/${s.route_id}`);
        routeCode = rRes.data?.route_code || "";
        routeName = rRes.data?.route_name || "";
      }

      // Pre-load bus codes and stops for this route_code
      if (routeCode) {
        const bcRes = await apiFetch(
          `/routes/bus-codes?route_code=${encodeURIComponent(routeCode)}`,
        );
        busCodesData = bcRes.data || [];
        setBusCodes(busCodesData);

        // Always fetch the route details to get stops
        let routeDetailsRes;
        if (busCodesData.length > 0) {
          routeDetailsRes = await apiFetch(
            `/routes/${busCodesData[0].route_id}`,
          );
        } else {
          // If no bus codes, fetch using the route_id directly
          routeDetailsRes = await apiFetch(`/routes/${s.route_id}`);
        }

        stopsData = routeDetailsRes.data?.stops || [];
        setStops(stopsData);

        // If stop_name wasn't in the student object, try to find it from the stops
        if (!stopName && s.stop_id) {
          const stopMatch = stopsData.find((st) => st.stop_id === s.stop_id);
          stopName = stopMatch?.stop_name || "";
        }
      }
    } catch (e) {
      console.error("Error loading student data:", e);
    }

    setFormData({
      student_id: s.student_id,
      registration_number: s.registration_number || "",
      full_name: s.full_name,
      grade: s.grade,
      route_code: routeCode,
      route_name: routeName,
      route_id: s.route_id || "",
      stop_id: s.stop_id || s.stop?.stop_id || "",
      stop_name: stopName,
      vehicle_code: s.vehicle_code || "",
      parent_id: s.parent_id || "",
    });
    setIsModalOpen(true);
  };

  const handleAddOpen = async () => {
    setFormData({
      student_id: null,
      registration_number: "",
      full_name: "",
      grade: "",
      route_code: "",
      route_name: "",
      route_id: "",
      stop_id: "",
      stop_name: "",
      vehicle_code: "",
      parent_id: "",
    });
    setParentName("");
    setParentEmail("");
    setPrimaryPhone("");
    setSecondaryPhone("");
    setIsModalOpen(true);

    try {
      const res = await apiFetch("/students/next-registration");
      setFormData((prev) => ({
        ...prev,
        registration_number: res.next_registration || "",
      }));
    } catch (err) {
      toast.error(err.message || "Failed to load next registration number");
    }
  };

  const handleDownloadStudents = () => {
    if (!students || students.length === 0) {
      alert("No students to download");
      return;
    }

    // Map students to exact Excel format
    const data = students.map((student) => ({
      "Student Name": student.full_name || "",
      "Registration Number": student.registration_number || "",
      Grade: student.grade || "",
      "Parent Name": student.parent_name || "",
      Relation: "",
      "Primary Phone": student.primary_phone || "",
      "Secondary Phone": student.secondary_phone || "",
      Route: student.route_name || "",
      Stop: student.stop_name || "",
      "Bus Code": student.bus_code || student.vehicle_code || "",
      Email: student.email || "",
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths
    ws["!cols"] = [
      { wch: 20 }, // Student Name
      { wch: 22 }, // Registration Number
      { wch: 12 }, // Grade
      { wch: 20 }, // Parent Name
      { wch: 15 }, // Relation
      { wch: 18 }, // Primary Phone
      { wch: 18 }, // Secondary Phone
      { wch: 30 }, // Route
      { wch: 25 }, // Stop
      { wch: 12 }, // Bus Code
      { wch: 28 }, // Email
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");

    // Download file
    const today = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `students_${today}.xlsx`);
  };

  const handleDeleteStudent = async (student) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently remove ${student.full_name}?\n\nThis will also delete their parent account if they have no other children enrolled.`,
    );
    if (!confirmed) return;

    try {
      await apiFetch(`/students/${student.student_id}`, {
        method: "DELETE",
      });
      setStudents((prev) =>
        prev.filter((item) => item.student_id !== student.student_id),
      );
      toast.success("Student removed successfully");
    } catch (err) {
      if (err.code === "STUDENT_ON_ACTIVE_TRIP") {
        toast.error(
          "Cannot remove student — their bus is currently on an active trip. Please try again after the trip is completed.",
        );
      } else {
        toast.error(err.message || "Failed to remove student");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (formData.student_id) {
        const data = {
          ...formData,
          route_id: formData.route_id || null,
          stop_id: formData.stop_id || null,
        };

        await apiFetch(`/students/${formData.student_id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        });
        toast.success("Student accurately updated");
      } else {
        const data = {
          full_name: formData.full_name,
          registration_number: formData.registration_number,
          grade: formData.grade,
          route_id: formData.route_id || null,
          stop_id: formData.stop_id || null,
          vehicle_code: formData.vehicle_code || "",
          parent_name: parentName,
          parent_email: parentEmail,
          primary_phone: primaryPhone,
          secondary_phone: secondaryPhone,
        };

        await apiFetch("/students", {
          method: "POST",
          body: JSON.stringify(data),
        });
        toast.success("Student successfully enrolled");
        setParentName("");
        setParentEmail("");
        setPrimaryPhone("");
        setSecondaryPhone("");
      }

      setIsModalOpen(false);
      loadStudents();
    } catch (err) {
      const message =
        err.message || err.error || err.code || "Failed saving logic.";
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      e.target.value = "";
      return;
    }

    setExcelLoading(true);
    setImportResults(null);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await apiFetch("/students/import", {
        method: "POST",
        body: formData,
      });
      const successCount = res.successCount || 0;
      const failedRows = res.failedRows || [];

      if (successCount) {
        toast.success(`${successCount} students imported successfully.`);
      }
      if (failedRows.length > 0) {
        toast.error(`${failedRows.length} rows failed. Check details below.`);
        console.log("Failed rows:", failedRows);
      }
      setImportResults({ successCount, failedRows });
      loadStudents();
    } catch (err) {
      const message = err.message || err?.code || "Failed importing Excel";
      toast.error(message);
    } finally {
      setExcelLoading(false);
      e.target.value = "";
    }
  };

  const filteredStudents = students.filter((s) => {
    const query = searchQuery.toLowerCase();
    return (
      (s.full_name || "").toLowerCase().includes(query) ||
      (s.registration_number || "").toString().toLowerCase().includes(query) ||
      (s.vehicle_code || "").toLowerCase().includes(query) ||
      (s.grade || "").toLowerCase().includes(query) ||
      (s.stop_name || s.stop?.stop_name || "").toLowerCase().includes(query)
    );
  });

  // Pagination
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(filteredStudents.length / ITEMS_PER_PAGE);
  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handlePrevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(totalPages, p + 1));

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h1 className="page-title text-gradient" style={{ margin: 0 }}>
          Student Roster
        </h1>
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            padding: "0 16px",
          }}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Search by name, reg no, grade or bus code..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              width: "100%",
              maxWidth: "400px",
              margin: 0,
              height: "40px",
            }}
          />
        </div>
        {user &&
          (user.role === "superadmin" ||
            user.role === "admin" ||
            user.role === "operator") && (
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <label
                className="btn-brown"
                style={{
                  margin: 0,
                  height: "40px",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                📊 Upload Excel
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelImport}
                  disabled={excelLoading}
                  style={{ display: "none" }}
                />
              </label>
              <a
                href="/student_import_template.xlsx"
                download="student_import_template.xlsx"
                className="btn-brown"
                style={{
                  height: "40px",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                ⬇ Excel Template
              </a>
              <button
                className="btn-brown"
                onClick={handleDownloadStudents}
                style={{ height: "40px" }}
              >
                ⬇ Download Students
              </button>
              <button
                className="btn-primary"
                onClick={handleAddOpen}
                style={{ height: "40px" }}
              >
                + Enroll Student
              </button>
            </div>
          )}
      </div>

      <div
        className="data-table-container glass-panel"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <table className="data-table" style={{ flex: 1, fontSize: "14px" }}>
          <thead>
            <tr style={{ height: "48px" }}>
              <th style={{ padding: "10px 16px" }}>Student Name</th>
              <th style={{ padding: "10px 16px" }}>Registration No.</th>
              <th style={{ padding: "10px 16px" }}>Grade</th>
              <th style={{ padding: "10px 16px" }}>Assigned Stop</th>
              <th style={{ padding: "10px 16px" }}>Bus Code</th>
              <th style={{ padding: "10px 16px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr style={{ height: "48px" }}>
                <td
                  colSpan="6"
                  style={{ textAlign: "center", padding: "10px 16px" }}
                >
                  Loading...
                </td>
              </tr>
            ) : filteredStudents.length === 0 ? (
              <tr style={{ height: "48px" }}>
                <td
                  colSpan="6"
                  style={{ textAlign: "center", padding: "10px 16px" }}
                >
                  No students found.
                </td>
              </tr>
            ) : (
              paginatedStudents.map((s) => (
                <tr key={s.student_id} style={{ height: "48px" }}>
                  <td style={{ fontWeight: "600", padding: "10px 16px" }}>
                    {s.full_name}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    {s.registration_number || "-"}
                  </td>
                  <td style={{ padding: "10px 16px" }}>{s.grade}</td>
                  <td style={{ padding: "10px 16px" }}>
                    {s.stop_name || s.stop?.stop_name || "-"}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    <span className="badge badge-active">
                      {s.vehicle_code || "-"}
                    </span>
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    <div className="table-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => handleEditOpen(s)}
                      >
                        Edit
                      </button>
                      {(user?.role === "superadmin" ||
                        user?.role === "admin" ||
                        user?.role === "operator") && (
                        <button
                          className="btn-danger"
                          onClick={() => handleDeleteStudent(s)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {filteredStudents.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
              padding: "16px",
              borderTop: "1px solid var(--border-color)",
            }}
          >
            <button
              className="btn-secondary"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              ← Previous
            </button>
            <span style={{ color: "var(--text-muted)" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn-secondary"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {importResults && (
        <div
          style={{
            marginBottom: "24px",
            padding: "16px",
            borderRadius: "12px",
            background: "rgba(255,255,255,0.08)",
          }}
        >
          <strong>Excel import summary:</strong>
          <div style={{ marginTop: "8px" }}>
            Imported: {importResults.successCount || 0}
          </div>
          {importResults.failedRows && importResults.failedRows.length > 0 && (
            <div style={{ marginTop: "8px", color: "var(--danger)" }}>
              Failed rows:
              <ul style={{ marginTop: "8px", paddingLeft: "18px" }}>
                {importResults.failedRows.map((row) => (
                  <li key={row.row}>
                    Row {row.row}: {row.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">
                {formData.student_id
                  ? "Edit Student File"
                  : "Enroll New Student"}
              </h2>
              <button
                className="modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                &times;
              </button>
            </div>
            {error && (
              <div
                style={{
                  background: "rgba(255,71,87,0.1)",
                  color: "var(--danger)",
                  padding: "12px",
                  borderRadius: "8px",
                  marginBottom: "16px",
                }}
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Registration Number</label>
                <input
                  type="text"
                  className="form-control"
                  name="registration_number"
                  value={formData.registration_number}
                  onChange={handleChange}
                  maxLength="20"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Grade</label>
                  <input
                    type="text"
                    className="form-control"
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              {!formData.student_id && (
                <>
                  <div className="form-group">
                    <label className="form-label">Parent Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Parent Name"
                      value={parentName}
                      onChange={(e) => setParentName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Parent Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="parent@email.com"
                      value={parentEmail}
                      onChange={(e) => setParentEmail(e.target.value)}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Primary Phone</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Primary Phone (required)"
                        value={primaryPhone}
                        onChange={(e) => setPrimaryPhone(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Secondary Phone</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Secondary Phone (optional)"
                        value={secondaryPhone}
                        onChange={(e) => setSecondaryPhone(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Select Route</label>
                  <select
                    className="form-control"
                    name="route_code"
                    value={formData.route_code}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Route --</option>
                    {/* Show current route even if not in list yet */}
                    {formData.route_code &&
                      !uniqueRoutes.find(
                        (r) => r.route_code === formData.route_code,
                      ) && (
                        <option value={formData.route_code}>
                          {formData.route_name}
                        </option>
                      )}
                    {uniqueRoutes.map((r) => (
                      <option key={r.route_code} value={r.route_code}>
                        {r.route_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Select Stop</label>
                  <select
                    className="form-control"
                    name="stop_id"
                    value={formData.stop_id}
                    onChange={handleChange}
                    disabled={!formData.route_code}
                    required
                  >
                    <option value="">-- Select Stop --</option>
                    {/* Show current stop even if not in list yet */}
                    {formData.stop_id &&
                      !stops.find((s) => s.stop_id === formData.stop_id) && (
                        <option value={formData.stop_id}>
                          {formData.stop_name}
                        </option>
                      )}
                    {stops.map((s) => (
                      <option key={s.stop_id} value={s.stop_id}>
                        {s.stop_name} (Seq {s.stop_sequence})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Bus Code</label>
                <select
                  className="form-control"
                  name="vehicle_code"
                  value={formData.vehicle_code}
                  onChange={handleChange}
                  disabled={
                    !formData.route_code ||
                    (busCodes.length === 0 && !formData.vehicle_code)
                  }
                  required
                >
                  <option value="">
                    {!formData.route_code
                      ? "— Select Route First —"
                      : busCodes.length === 0 && !formData.vehicle_code
                        ? "No bus codes assigned to this route"
                        : "— Select Bus Code —"}
                  </option>
                  {/* Show current bus code even if not in list yet */}
                  {formData.vehicle_code &&
                    !busCodes.find(
                      (b) => b.vehicle_code === formData.vehicle_code,
                    ) && (
                      <option value={formData.vehicle_code}>
                        {formData.vehicle_code}
                      </option>
                    )}
                  {busCodes.map((b) => (
                    <option key={b.vehicle_code} value={b.vehicle_code}>
                      {b.vehicle_code}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                className="btn-primary btn-block"
                disabled={submitting}
              >
                {submitting ? "Connecting..." : "Submit Records"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;
